/**
 * Absences — Vue globale absences/congés (parité BE-TAYE AbsencesList.jsx).
 * Filtres statut/type, actions valider/refuser, dialog création.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { DatePickerFR } from "@/components/ui/date-picker-fr";
import { Input } from "@/components/ui/input";
import {
  Calendar, Check, X, Clock, Plus, Filter, Trash2,
} from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = {
  cp: "Congés payés", rtt: "RTT", maladie: "Arrêt maladie",
  sans_solde: "Sans solde", famille: "Événement familial", autre: "Autre",
};
const TYPE_STYLE = {
  cp: "bg-blue-50 text-blue-700 border-blue-200",
  rtt: "bg-violet-50 text-violet-700 border-violet-200",
  maladie: "bg-red-50 text-red-700 border-red-200",
  sans_solde: "bg-slate-100 text-slate-700 border-slate-300",
  famille: "bg-pink-50 text-pink-700 border-pink-200",
  autre: "bg-amber-50 text-amber-700 border-amber-200",
};
const STATUS = {
  pending:  { cls: "bg-amber-100 text-amber-700 border-amber-200", label: "À valider", icon: Clock },
  approved: { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Validée", icon: Check },
  rejected: { cls: "bg-red-100 text-red-700 border-red-200", label: "Refusée", icon: X },
};

function NewAbsenceDialog({ open, onOpenChange, onSaved }) {
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({
    employee_id: "", type: "cp", start_date: "", end_date: "", reason: "", notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/hr/employees").then(r => setEmployees(r.data.employees || []))
        .catch(e => toast.error(apiError(e)));
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.employee_id || !form.start_date || !form.end_date) {
      toast.error("Salarié, date début et date fin sont requis"); return;
    }
    setSaving(true);
    try {
      const { employee_id, ...body } = form;
      await api.post(`/hr/employees/${employee_id}/absences`, body);
      toast.success("Absence créée");
      onSaved?.(); onOpenChange(false);
      setForm({ employee_id: "", type: "cp", start_date: "", end_date: "", reason: "", notes: "" });
    } catch (e2) { toast.error(apiError(e2)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-absence-dialog">
        <DialogHeader><DialogTitle>Nouvelle absence</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Salarié *</Label>
            <Select value={form.employee_id} onValueChange={(v) => setForm(p => ({ ...p, employee_id: v }))}>
              <SelectTrigger data-testid="absence-employee-select"><SelectValue placeholder="Choisir..." /></SelectTrigger>
              <SelectContent>{employees.map(e => (
                <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>
              ))}</SelectContent>
            </Select>
          </div>
          <div><Label>Type *</Label>
            <Select value={form.type} onValueChange={(v) => setForm(p => ({ ...p, type: v }))}>
              <SelectTrigger data-testid="absence-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(TYPE_LABEL).map(([v, l]) => (
                <SelectItem key={v} value={v}>{l}</SelectItem>
              ))}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Début *</Label>
              <DatePickerFR value={form.start_date} onChange={(v) => setForm(p => ({ ...p, start_date: v }))} />
            </div>
            <div><Label>Fin *</Label>
              <DatePickerFR value={form.end_date} onChange={(v) => setForm(p => ({ ...p, end_date: v }))} />
            </div>
          </div>
          <div><Label>Motif</Label>
            <Input value={form.reason} onChange={(e) => setForm(p => ({ ...p, reason: e.target.value }))}
              placeholder="Ex: vacances d'été, mariage..." />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="save-absence-btn">
              {saving ? "..." : "Créer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Absences() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("hr.write") || hasPermission("admin");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (type !== "all") params.type = type;
      const r = await api.get("/hr/absences", { params });
      setRows(r.data.absences || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, type]);

  const approve = async (id) => {
    try { await api.post(`/hr/absences/${id}/approve`); toast.success("Absence validée"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const reject = async (id) => {
    try { await api.post(`/hr/absences/${id}/reject`); toast.success("Absence refusée"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer cette absence ?")) return;
    try { await api.delete(`/hr/absences/${id}`); toast.success("Absence supprimée"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const pending = rows.filter(r => r.status === "pending").length;

  return (
    <div className="space-y-6" data-testid="absences-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Absences & Congés
          </h1>
          <p className="text-sm text-slate-500">
            {rows.length} enregistrement{rows.length > 1 ? "s" : ""} · {pending} à valider
          </p>
        </div>
        {canWrite && (
          <Button onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="new-absence-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Nouvelle absence
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Filter className="w-4 h-4" /> Filtres
            </div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                <SelectItem value="pending">À valider</SelectItem>
                <SelectItem value="approved">Validées</SelectItem>
                <SelectItem value="rejected">Refusées</SelectItem>
              </SelectContent>
            </Select>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous types</SelectItem>
                {Object.entries(TYPE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">{[...Array(5)].map((_, i) =>
              <div key={i} className="h-10 bg-slate-100 animate-pulse rounded" />)}</div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center" data-testid="absences-empty">
              <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Aucune absence enregistrée.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase text-slate-500">Salarié</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Type</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Période</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500 text-right">Jours</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Statut</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((a, i) => {
                  const st = STATUS[a.status] || STATUS.pending;
                  const Icon = st.icon;
                  return (
                    <motion.tr key={a.id}
                      initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(i, 10) * 0.02, duration: 0.2 }}
                      className="border-b hover:bg-slate-50"
                      data-testid={`absence-row-${i}`}>
                      <TableCell>
                        <div className="font-medium text-slate-900 text-sm">{a.employee_name}</div>
                        <div className="text-xs text-slate-500">{a.job_title || "—"}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs ${TYPE_STYLE[a.type] || TYPE_STYLE.autre}`}>
                          {TYPE_LABEL[a.type] || a.type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm font-mono">
                        {new Date(a.start_date).toLocaleDateString("fr-FR")} → {new Date(a.end_date).toLocaleDateString("fr-FR")}
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold">{a.days_count?.toFixed(1) || "—"}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={st.cls}>
                          <Icon className="w-3 h-3 mr-1" />{st.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-1">
                          {canWrite && a.status === "pending" && (
                            <>
                              <Button size="sm" variant="ghost"
                                className="h-7 text-emerald-600" onClick={() => approve(a.id)}
                                data-testid={`approve-${a.id}`}>
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost"
                                className="h-7 text-red-600" onClick={() => reject(a.id)}
                                data-testid={`reject-${a.id}`}>
                                <X className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {canWrite && (
                            <Button size="sm" variant="ghost"
                              className="h-7 text-slate-500" onClick={() => remove(a.id)}
                              data-testid={`del-abs-${a.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </motion.tr>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <NewAbsenceDialog open={open} onOpenChange={setOpen} onSaved={load} />
    </div>
  );
}
